import {
  all,
  fork,
  takeLeading,
  delay,
  put,
  call,
  select,
} from 'redux-saga/effects';
import * as Actions from './actions';
import * as requests from '../../helpers/HTTPRequest';
import ApiConstants from '../../helpers/ApiConstants';
import { checkValidToken } from 'src/common/helpers/CheckAuth';
import TextConstants from 'src/common/helpers/TextConstants';
import { getDashboardSettings } from 'src/views/data/dashboard/actions';
import { toastError, toastWarning } from 'src/common/helpers/Notifications';

// Helper function to calculate URL length for a batch of device IDs
function calculateUrlLength(baseUrl, deviceIds) {
  const params = new URLSearchParams();
  deviceIds.forEach((deviceId) => {
    params.append('deviceId', deviceId);
  });
  return `${baseUrl}?${params.toString()}`.length;
}

// Helper function to create batches that respect URL length limits
function createBatches(deviceIds, baseUrl, maxUrlLength = 8000) {
  const batches = [];
  let currentBatch = [];

  for (const deviceId of deviceIds) {
    const testBatch = [...currentBatch, deviceId];
    const urlLength = calculateUrlLength(baseUrl, testBatch);

    if (urlLength > maxUrlLength && currentBatch.length > 0) {
      // Current batch would exceed limit, save it and start new batch
      batches.push(currentBatch);
      currentBatch = [deviceId];
    } else {
      // Add to current batch
      currentBatch.push(deviceId);
    }
  }

  // Add the last batch if it has items
  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
}

function* getLatestData({ payload }) {
  try {
    // check valid token
    yield checkValidToken();

    const { datasets } = payload;
    if (datasets && datasets.length > 0) {
      const baseUrl = `${ApiConstants.BASE_URL}${ApiConstants.LAST_DATA}`;

      // Create batches to respect URL length limits
      const batches = createBatches(datasets, baseUrl);

      // Process all batches in parallel
      const batchPromises = batches.map(function* (batch) {
        const params = new URLSearchParams();
        batch.forEach((deviceId) => {
          params.append('deviceId', deviceId);
        });
        const url = `${baseUrl}?${params.toString()}`;

        return yield call(requests.getRequest, url, {});
      });

      const batchResults = yield all(batchPromises);

      let mergedData = {};
      batchResults.forEach((batchResult) => {
        if (batchResult && typeof batchResult === 'object') {
          mergedData = { ...mergedData, ...batchResult };
        }
      });

      yield put(Actions.getLatestDataSuccess(mergedData));
    } else {
      // toastWarning('NO DEVICES FOUND!');
      console.log('WARNING: NO DEVICES FOUND!');
    }
  } catch (e) {
    // When device id unregistered. Reload devices...
    if (e.status === 404) {
      const currentDevices = yield select((state) => state.UnitsChannels.units);
      const deviceIdsUnregistered = e.data?.devicesUnregistered || [];
      const devicesNameArr = [];
      deviceIdsUnregistered.forEach((id) => {
        const dv = currentDevices.find((item) => item.deviceId === id);
        const deviceName =
          dv && dv.name ? dv.name : `${TextConstants.Common.NoName}(id: ${id})`;
        devicesNameArr.push(deviceName);
      });
      const devicesName = devicesNameArr.join('、') + '。';
      toastWarning(
        `${e.message}${TextConstants.Common.DeviceNotRegistered}${devicesName}`
      );
      yield put(getDashboardSettings());
    } else {
      toastError(e.message, TextConstants.Common.Warning);
      yield put(Actions.getLatestDataError('loading failed'));
    }
  } finally {
    yield delay(500);
    yield put(Actions.getLatestDataCleanState());
  }
}

export function* latestData() {
  yield takeLeading(Actions.latestDataActions.GET_LATEST_DATA, getLatestData);
}

export default function* rootSaga() {
  yield all([fork(latestData)]);
}
